import { Component, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TrackerElevation} from '../../../../models/tracker';
import { selectVolume } from '../../../../features/volume/volume.actions';
import {
  selectDetection
} from '../../../../features/detection/detection.actions';
import {
  selectCut
} from '../../../../features/cut/cut.actions';
import {
  selectMeasure} from '../../../../features/measure/measure.actions';
import { MatDialog } from '@angular/material/dialog';
import {
  DeleteConfirmDialog
} from '../../../dialogs/delete-confirm-dialog/delete-confirm.dialog';
import { RenameDialog } from '../../../dialogs/rename-dialog/rename.dialog';
import {
  setCameraConfiguration,
  setCameraMode,
  setMapHandleKeyboard
} from '../../../../features/cesium/cesium.actions';
import { ExportTools } from '../../../../tools/export.tools';
import { CesiumTools } from '../../../../tools/cesium.tools';
import { CameraMode } from '../../../../enums/camera';
import { selectCurrentSite } from '../../../../features/site/site.selectors';
import { first } from 'rxjs';
import { Site } from '../../../../models/site';
import { ApiService } from '../../../../services/api.service';
import { TranslocoService } from '@jsverse/transloco';
import { selectUser } from '../../../../features/user/user.selectors';
import {
  ElevationChartComponent
} from '../../charts/elevation-chart.component';
import {
  selectCurrentTrackerElevation, selectElevationShowById
} from '../../../../features/tracker/tracker.selectors';
import {
  deleteTracker, selectTrackerElevation,
  selectTrackerImage, setTrackerShow, updateTracker
} from '../../../../features/tracker/tracker.actions';
import { Actions } from '@ngrx/effects';
import {
  selectConfigDisplayElevations,
  selectConfigItemDetailExpanded,
} from '../../../../features/config/config.selectors';
import {
  setConfigExpandItemDetail
} from '../../../../features/config/config.actions';


@Component({
    selector: 'elevation-details',
    templateUrl: './elevation-details.component.html',
    styleUrls: ['../details.component.scss', './elevation-details.component.scss'],
    standalone: false
})
export class ElevationDetailsComponent {

  elevation: TrackerElevation | null | undefined;
  @ViewChild('graph') graph: ElevationChartComponent | undefined;

  readonly displayElevations$ = this.store.select(selectConfigDisplayElevations);
  readonly isExpanded$ = this.store.select(selectConfigItemDetailExpanded);
  readonly currentUser$ = this.store.select(selectUser);

  constructor(private store: Store,
              private actions: Actions,
              private api: ApiService,
              private dialog: MatDialog,
              private translocoService: TranslocoService) {
    this.store.select(selectCurrentTrackerElevation)
      .pipe(takeUntilDestroyed())
      .subscribe((elevation: TrackerElevation | null | undefined) => {
        this.elevation = elevation;
        if (this.elevation) {
          this.store.dispatch(selectMeasure({id: undefined}));
          this.store.dispatch(selectVolume({id: undefined}));
          this.store.dispatch(selectDetection({id: undefined}));
          this.store.dispatch(selectCut({id: undefined}));
          this.store.dispatch(selectTrackerImage({id: undefined}));
        }
      });
  }

  onHandleFocus(): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: false}));
  }

  onFocusLeave(): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: true}));
  }

  onClickFlyTo(elevation: TrackerElevation): void {
    const cameraConfiguration = CesiumTools.cameraConfigurationFromTargetAndDirection(elevation.center, elevation.cameraDirection, elevation.length);
    if (!cameraConfiguration) return;
    this.store.dispatch(setCameraMode({cameraMode: CameraMode.MODE_3D}));
    this.store.dispatch(setCameraConfiguration({cameraConfiguration}));
  }

  onClickClose(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
    this.store.dispatch(selectTrackerElevation({id: undefined}));
  }

  onClickExpand(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: true}));
  }

  onClickCollapse(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
  }

  onClickExportData(elevation: TrackerElevation): void {
    ExportTools.exportJSON(elevation.name, JSON.stringify(elevation));
  }

  onClickDelete(elevation: TrackerElevation): void {
    const deleteDialogRef = this.dialog.open(DeleteConfirmDialog, {
      disableClose: false
    });
    deleteDialogRef.componentInstance.message = this.translocoService.translate("would_you_like_to_delete", {name: elevation.name});
    deleteDialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.store.select(selectCurrentSite)
          .pipe(first())
          .subscribe((site: Site | null | undefined) => {
            if (site) {
              this.store.dispatch(selectTrackerElevation({id: undefined}));
              this.store.dispatch(deleteTracker({
                organizationId: site.organization,
                siteId: site.id,
                trackerId: elevation.id
              }));
            }
          });
      }
    });
  }

  onClickRename(elevation: TrackerElevation): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: false}));
    const renameDialogRef = this.dialog.open(RenameDialog, {
      disableClose: false
    });
    renameDialogRef.componentInstance.placeholder = elevation.name;
    renameDialogRef.afterClosed().subscribe(result => {
      this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: true}));
      if(result && this.elevation) {
        this.store.select(selectCurrentSite)
          .pipe(first())
          .subscribe((site: Site | null | undefined) => {
            if (site) {
              this.store.dispatch(updateTracker({
                organizationId: site.organization,
                siteId: site.id,
                trackerId: elevation.id,
                payload: {
                  name: result,
                  is_shared: elevation.isShared
                }}));
            }
          });
      }
    });
  }

  onClickToggleShare(elevation: TrackerElevation): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (site) {
          this.store.dispatch(updateTracker({
            organizationId: site.organization,
            siteId: site.id,
            trackerId: elevation.id,
            payload: {
              name: elevation.name,
              is_shared: !elevation.isShared
            }}));
        }
      });
  }

  onClickToggleShow(elevation: TrackerElevation): void {
    this.store.dispatch(setTrackerShow({id: elevation.id, show: !elevation.show}));
  }
}
